import React, { FC, SyntheticEvent, useCallback, useEffect, useMemo, useState } from 'react';
import LoadingButton from '@mui/lab/LoadingButton';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import { styled, useTheme } from '@mui/material/styles';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import {
  GridColDef,
  GridRowClassNameParams,
  GridRowId,
  GridRowParams,
  GridRowSelectionModel,
  GridSortModel,
  GridValueFormatterParams,
} from '@mui/x-data-grid-pro';
import isEqual from 'lodash/isEqual';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { INotificationDTO } from 'src/api/dtos';
import NotificationsRepository from 'src/api/repositories/common/v1/notifications.repository';
import { IError } from 'src/api/types';
import { AttachmentIcon } from 'src/assets/img/icons';
import { formatUserEmployeeName } from 'src/features/message-center/MessageCenter.utils';
import { useMessageCenterQuery, useMessageCenterSelection } from 'src/features/message-center/providers';
import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon, DataGridCrud } from 'src/shared/components';
import { Modal } from 'src/shared/components';
import { DAY_DATE_FORMAT } from 'src/shared/constants';
import { MailboxFolderEnums, PermissionsEnums } from 'src/shared/enums';
import { ComponentWithPermissions } from 'src/shared/hocs';
import { useNavigateByQueryParams, usePrevious } from 'src/shared/hooks';
import { useTranslate } from 'src/shared/providers';
import { formatDateTime, getErrorMessage, getKeyByValue, mapSortDirectionEnums } from 'src/shared/utils';
import { getTimeFormat } from 'src/store/user/user.selectors';

import { MessageCenterListTabEnums, NotificationsColumnEnums } from './MessageCenterList.enums';
import { defaultNotificationsFilters, mapNotificationsSortByToColumnEnums } from './MessageCenterList.utils';

const NOTIFICATIONS_POLLING_IN_MILLISECONDS = 10000;

export const MessageCenterListTabs: FC = () => {
  const { t } = useTranslate();
  const theme = useTheme();
  const navigateByQueryParams = useNavigateByQueryParams();
  const timeFormat = useSelector(getTimeFormat);

  const { filters } = useMessageCenterQuery();
  const { selected, onSelect, onClear } = useMessageCenterSelection();

  const [page, setPage] = useState<number>(1);
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowId[]>([]);
  const [actionsMenu, setActionsMenu] = useState<null | HTMLElement>(null);
  const actionsMenuOpen = Boolean(actionsMenu);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const [updateNotifications, { isLoading: isUpdating }] =
    NotificationsRepository.endpoints.useUpdateNotificationsMutation();

  const [deleteNotifications, { isLoading: isDeleting }] =
    NotificationsRepository.endpoints.useDeleteNotificationsMutation();

  const {
    data: paginatedData,
    isLoading,
    isFetching,
  } = NotificationsRepository.endpoints.useGetNotificationsQuery(
    {
      ...filters,
      searchTerm: (filters.searchTerm?.length ?? 0) > 1 ? filters.searchTerm : undefined,
      page,
      perPage: 10,
    },
    {
      pollingInterval: NOTIFICATIONS_POLLING_IN_MILLISECONDS,
    },
  );

  const loading = isLoading || isFetching;

  const markAsDisabled =
    !rowSelectionModel.length || isUpdating || isDeleting || filters.mailboxFolder === MailboxFolderEnums.OUT;

  const deleteDisabled = !rowSelectionModel.length || isUpdating || isDeleting;

  const columns: GridColDef<INotificationDTO>[] = useMemo(
    () => [
      {
        field: NotificationsColumnEnums.SENDER,
        headerName:
          filters.mailboxFolder === MailboxFolderEnums.OUT ? t('messageCenter.to') : t('messageCenter.sender'),
        flex: 1,
        sortable: true,
        valueFormatter: (params: GridValueFormatterParams<INotificationDTO['fromUser']>) =>
          params.value ? formatUserEmployeeName(params.value) : '',
        cellClassName: 'senderCell',
      },
      {
        field: NotificationsColumnEnums.SUBJECT,
        headerName: t('global.subject'),
        flex: 1,
        sortable: true,
        cellClassName: 'subjectCell',
      },
      {
        field: NotificationsColumnEnums.DATE,
        headerName: t('global.date'),
        flex: 1.1,
        sortable: true,
        renderCell: (params) => (
          <Stack direction="row" alignItems="center" justifyContent={'space-between'} gap={1} width={'100%'}>
            <Typography fontSize={'0.75rem'}>{formatDateTime(params.value, DAY_DATE_FORMAT, timeFormat)}</Typography>
            {!!params.row.attachmentCount && (
              <AttachmentIcon fill={theme.palette.secondary.main} width={20} height={20} />
            )}
          </Stack>
        ),
        cellClassName: 'dateCell',
      },
    ],
    [filters, timeFormat],
  );

  const handleTabChange = (_: SyntheticEvent, tab: MessageCenterListTabEnums) => {
    navigateByQueryParams({ mailboxFolder: tab });
    setRowSelectionModel([]);
  };

  const handleRowClick = (params: GridRowParams<INotificationDTO>) => {
    onSelect(params.row);
  };

  const sortModel = useMemo<GridSortModel>(() => {
    const sortBy = filters.sortBy ?? defaultNotificationsFilters.sortBy;
    const sortDirection = filters.sortDirection ?? defaultNotificationsFilters.sortDirection;

    const field = mapNotificationsSortByToColumnEnums[sortBy];
    const sort = mapSortDirectionEnums[sortDirection];

    return [
      {
        field,
        sort,
      },
    ];
  }, [filters]);

  const handleSortModelChange = useCallback(
    (model: GridSortModel) => {
      if (!model?.length) {
        return;
      }

      const { field, sort } = model[0];

      if (!field || !sort) {
        return;
      }

      navigateByQueryParams({
        sortBy: getKeyByValue(mapNotificationsSortByToColumnEnums, field),
        sortDirection: getKeyByValue(mapSortDirectionEnums, sort),
      });
    },
    [navigateByQueryParams],
  );

  const handleRowSelectionModelChange = (selectionModel: GridRowSelectionModel) => {
    setRowSelectionModel(selectionModel as GridRowId[]);
  };

  const getRowClassName = (params: GridRowClassNameParams<INotificationDTO>) => {
    const classNameList: string[] = [];

    if (!params.row.isRead && filters.mailboxFolder !== MailboxFolderEnums.OUT) {
      classNameList.push('isUnreadCell');
    }

    return classNameList.join(' ');
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setActionsMenu(event.currentTarget);
  };

  const handleMenuClose = () => {
    setActionsMenu(null);
  };

  const handleMarkAllAs = async (read: boolean) => {
    if (!rowSelectionModel.length) {
      return;
    }

    try {
      await updateNotifications({ ids: rowSelectionModel.map((id) => +id), read }).unwrap();

      if (read) {
        toast.info(t('messageCenter.youHaveSuccessfullyMarkedNumberMessageSRead', rowSelectionModel.length), {
          type: toast.TYPE.SUCCESS,
        });
      } else {
        toast.info(t('messageCenter.youHaveSuccessfullyMarkedNumberMessageSUnread', rowSelectionModel.length), {
          type: toast.TYPE.SUCCESS,
        });
      }
    } catch (error) {
      const errorMessage = getErrorMessage(error as IError, t('global.oopsAnErrorOccurred'));
      toast.info(errorMessage, { type: toast.TYPE.ERROR });
    } finally {
      setRowSelectionModel([]);
      handleMenuClose();
    }
  };

  const openDeleteModal = () => {
    setDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
  };

  const submitDeleteModal = async () => {
    closeDeleteModal();

    if (!rowSelectionModel.length) {
      return;
    }

    try {
      await deleteNotifications({ ids: rowSelectionModel.map((id) => +id) }).unwrap();

      toast.info(t('messageCenter.youHaveSuccessfullyDeletedNumberMessageS', rowSelectionModel.length), {
        type: toast.TYPE.SUCCESS,
      });

      if (selected && rowSelectionModel.includes(selected?.id)) {
        onClear();
      }
    } catch (error) {
      const errorMessage = getErrorMessage(error as IError, t('global.oopsAnErrorOccurred'));
      toast.info(errorMessage, { type: toast.TYPE.ERROR });
    } finally {
      setRowSelectionModel([]);
      handleMenuClose();
    }
  };

  const previousFilters = usePrevious(filters);

  useEffect(() => {
    if (isEqual(filters, previousFilters)) {
      return;
    }

    setPage(1);
    onClear();
  }, [filters, previousFilters]);

  return (
    <>
      <Stack direction="column" gap={2} flexGrow={1} minHeight={0}>
        <Stack direction="row" gap={2} justifyContent="space-between">
          <Tabs
            value={filters.mailboxFolder}
            onChange={handleTabChange}
            sx={{ '&, button': { height: '38px', minHeight: '38px', textTransform: 'none' } }}
          >
            <Tab value={MessageCenterListTabEnums.INBOX} id="inbox-tab" label={t('messageCenter.inbox')} />
            <Tab value={MessageCenterListTabEnums.UNREAD} id="unred-tab" label={t('messageCenter.unread')} />
            <Tab value={MessageCenterListTabEnums.SENT} id="sent-tab" label={t('messageCenter.sent')} />
          </Tabs>
          <LoadingButton size="small" startIcon={<ChevronDownIcon />} onClick={handleMenuOpen}>
            {t('global.actions')}
          </LoadingButton>
          <Menu
            anchorEl={actionsMenu}
            open={actionsMenuOpen}
            onClose={handleMenuClose}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            sx={{ mt: 1 }}
            slotProps={{ paper: { sx: { width: 250 } } }}
          >
            <ComponentWithPermissions permissions={PermissionsEnums.NOTIFICATIONS_EDIT}>
              <MenuItem disabled={markAsDisabled} onClick={() => handleMarkAllAs(false)}>
                <Typography variant="body2">{t('messageCenter.markAllAsUnread')}</Typography>
              </MenuItem>
            </ComponentWithPermissions>
            <MenuItem disabled={markAsDisabled} onClick={() => handleMarkAllAs(true)}>
              <Typography variant="body2">{t('messageCenter.markAllAsRead')}</Typography>
            </MenuItem>
            <ComponentWithPermissions permissions={PermissionsEnums.NOTIFICATIONS_EDIT}>
              <MenuItem disabled={deleteDisabled} onClick={openDeleteModal}>
                <Typography variant="body2" color="error">
                  {t('global.deleteAll')}
                </Typography>
              </MenuItem>
            </ComponentWithPermissions>
          </Menu>
        </Stack>
        <Stack flex={1} overflow="auto">
          <StyledDataGridCrud
            columns={columns}
            rows={paginatedData?.data ?? []}
            loading={loading || isUpdating || isDeleting}
            // Selection
            selectedRowId={selected?.id}
            onRowClick={handleRowClick}
            // Sorting
            sortingMode="server"
            sortingOrder={['asc', 'desc']}
            sortModel={sortModel}
            onSortModelChange={handleSortModelChange}
            // Checkbox
            checkboxSelection
            rowSelectionModel={rowSelectionModel}
            onRowSelectionModelChange={handleRowSelectionModelChange}
            keepNonExistentRowsSelected
            // Styles
            getRowClassName={getRowClassName}
            columnHeaderHeight={40}
            rowHeight={56}
            sx={{ '.MuiDataGrid-main': { outline: 'none' } }}
            _container={{ height: '100%' }}
          />
        </Stack>
        <Stack direction="row" gap={2} justifyContent="space-between" alignItems="center">
          <Typography variant="caption" color="text.secondary">
            {paginatedData
              ? t('messageCenter.showingFromtoOfTotalMessages', {
                  from: paginatedData.from,
                  to: paginatedData.to,
                  total: paginatedData.total,
                })
              : t('global.loading')}
          </Typography>
          <Stack direction="row" gap={1}>
            <IconButton size="small" onClick={() => setPage(page - 1)} disabled={page <= 1}>
              <ChevronLeftIcon />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => setPage(page + 1)}
              disabled={paginatedData ? page >= paginatedData.pageCount : true}
            >
              <ChevronRightIcon />
            </IconButton>
          </Stack>
        </Stack>
      </Stack>
      <Modal
        open={deleteModalOpen}
        onClose={closeDeleteModal}
        modalHeaderProps={{ title: t('messageCenter.deleteMessages'), onClose: closeDeleteModal }}
        modalFooterProps={{
          submitText: t('global.delete'),
          onSubmit: submitDeleteModal,
          closeText: t('global.cancel'),
          onClose: closeDeleteModal,
          loading: isDeleting,
          disabled: loading,
        }}
      >
        <Stack direction="column" gap={2}>
          <Typography>{t('messageCenter.youAreDeletingNumberMessageS', rowSelectionModel.length)}</Typography>
          <Typography>{t('messageCenter.areYouSureYouWantToDeleteTheseMessages')}</Typography>
        </Stack>
      </Modal>
    </>
  );
};

const StyledDataGridCrud = styled(DataGridCrud)(() => ({
  // ROW
  '& .MuiDataGrid-row.isUnreadCell': {
    fontWeight: 'bold',
  },
  // CELL
  '& .MuiDataGrid-cell.subjectCell, & .MuiDataGrid-cell.dateCell': {
    fontSize: '0.75rem',
  },
})) as typeof DataGridCrud;
